import { useCallback, useEffect, useRef } from "react";

const DEFAULT_UNLOCK_MS = 400;

/**
 * Synchronous lock for click/submit handlers.
 * Blocks a second action until `isBusy` (e.g. mutation.isLoading) clears,
 * or until `unlockDelayMs` if busy never starts (covers the React state race).
 */
export function useActionLock(isBusy = false, unlockDelayMs = DEFAULT_UNLOCK_MS) {
  const lockRef = useRef(false);
  const busyRef = useRef(isBusy);
  const sawBusyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  busyRef.current = isBusy;

  useEffect(() => {
    if (isBusy) {
      sawBusyRef.current = true;
      lockRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      return;
    }

    if (sawBusyRef.current) {
      lockRef.current = false;
      sawBusyRef.current = false;
    }
  }, [isBusy]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return useCallback(() => {
    if (lockRef.current) {
      return false;
    }
    lockRef.current = true;
    sawBusyRef.current = false;
    timerRef.current = setTimeout(() => {
      if (!busyRef.current && !sawBusyRef.current) {
        lockRef.current = false;
      }
    }, unlockDelayMs);
    return true;
  }, [unlockDelayMs]);
}
